import type { useTranslation } from 'react-i18next';

import type { BoardAlert } from '@/api/generated/models';
import { ContractStatus } from '@/api/generated/models/contractStatus';
import { statusConfig as contractStatusConfig } from '@/features/contracts/contractDisplay';
import i18n from '@/i18n';
import { currentLocale, formatAmount } from '@/utils/format';

/** Mora odgovarati BoardPeriod enumu na backendu (com.externo.cxm.board.BoardPeriod). */
export const BOARD_PERIOD_OPTIONS = [
  { value: 'LAST_30_DAYS', labelKey: 'board.period.last30Days' },
  { value: 'LAST_3_MONTHS', labelKey: 'board.period.last3Months' },
  { value: 'LAST_6_MONTHS', labelKey: 'board.period.last6Months' },
  { value: 'THIS_YEAR', labelKey: 'board.period.thisYear' },
  { value: 'ALL', labelKey: 'board.period.all' },
  { value: 'CUSTOM', labelKey: 'board.period.custom' }
];

export const buildPeriodOptions = () =>
  BOARD_PERIOD_OPTIONS.map((option) => ({ value: option.value, label: i18n.t(option.labelKey) }));

/** Tro slovni naziv mjeseca (Figma: "Tra", "Svi", "Lip"...) — lokaliziran, ne fiksno hrvatski. */
export const monthAbbrev = (year: number, month: number): string => {
  const label = new Date(year, month - 1, 1).toLocaleDateString(currentLocale(), {
    month: 'short'
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** Semafor pragovi za "Realizacija po ugovoru (%)" — fiksni, ne konfigurabilno po organizaciji. */
export const realizationRowColor = (percent: number): string => {
  if (percent >= 90) return 'var(--chart-realization-good)';
  if (percent >= 80) return 'var(--color-status-warning)';
  return 'var(--color-status-danger)';
};

/**
 * Za Board "Status radnih paketa" graf — "late" nije pravi status u bazi, backend ga izvodi
 * dijeljenjem 'active' po valid_to (vidi BoardDaoImpl.findWorkPackageStatusCounts).
 */
export const workPackageStatusLabelKey = (status: string): string => {
  switch (status) {
    case 'active':
      return 'board.wpStatus.active';
    case 'late':
      return 'board.wpStatus.late';
    case 'completed':
      return 'board.wpStatus.completed';
    case 'cancelled':
      return 'board.wpStatus.cancelled';
    default:
      return 'board.wpStatus.draft';
  }
};

/** contracts.status dolazi s Board SQL-a lowercase (isti set vrijednosti kao ContractStatus enum) — pretvori pa reusaj postojeći statusConfig (contractDisplay.ts) umjesto nagađanja novih i18n ključeva. */
export const contractRowStatusConfig = (status: string) => {
  const enumStatus = status.toUpperCase() as ContractStatus;
  return contractStatusConfig[enumStatus] ?? contractStatusConfig[ContractStatus.ACTIVE];
};

/**
 * Backend namjerno šalje STRUKTURIRANE podatke za svako upozorenje (ne gotovu poruku) — isti
 * princip kao ostatak aplikacije (backend messages.properties je samo za greške). Ovdje se
 * sastavlja konačan tekst preko t() s interpolacijom.
 */
export const formatBoardAlert = (
  t: ReturnType<typeof useTranslation>['t'],
  alert: BoardAlert
): string => {
  switch (alert.type) {
    case 'CONTRACT_BELOW_PLAN':
      return t('board.alerts.contractBelowPlan', {
        contractNumber: alert.contractNumber,
        percent: (alert.belowPlanPercent ?? 0).toFixed(0)
      });
    case 'CONTRACT_NOT_STARTED':
      return t('board.alerts.contractNotStarted', { contractNumber: alert.contractNumber });
    case 'CLIENT_OVERDUE_AMOUNT':
      return t('board.alerts.clientOverdueAmount', {
        clientOrgName: alert.clientOrgName,
        amount: formatAmount(alert.overdueAmount),
        count: alert.overdueInvoiceCount
      });
    case 'CLIENT_LATE_CONTRACTS':
      return t('board.alerts.clientLateContracts', {
        clientOrgName: alert.clientOrgName,
        count: alert.lateContractCount
      });
    case 'WEEK_REPORT_WAITING_ON_CLIENT':
      return t('board.alerts.weekReportWaiting', {
        contractNumber: alert.contractNumber,
        days: alert.waitingDays
      });
    default:
      return '';
  }
};
